/**
 * ticketService.ts
 * 
 * Servicio unificado de tickets: generación de payload, resolución de config,
 * renderizado HTML para impresión y preview, generación de barcode.
 * 
 * NO modifica lógica de venta. Solo opera POST-VENTA.
 */

import { fetchAPI } from './apiClient';

// ─── Tipos ───────────────────────────────────────────────

export type TicketAncho = 80 | 58;
export type TicketFontWeight = 'normal' | 'bold' | 'extra-bold';
export type TicketFontSize = 'normal' | 'grande' | 'extra-grande' | 'gigante' | 'extra-gigante';

export interface TicketConfig {
  nombre: string;
  notas: string;
  ancho: TicketAncho;
  logoUrl: string | null;
  logoDataUrl: string | null;
  mostrarLogo: boolean;
  fontWeight: TicketFontWeight;
  fontSize: TicketFontSize;
  // ── FASE 1: RRR Configuración LOCAL (Logo + Ticket)
  nombreLocal?: string;  // nombre_ticket del local (fallback a localNombre del payload)
  leyendaLocal?: string; // leyenda_ticket del local (fallback a "GRACIAS POR SU PREFERENCIA")
  tipoImpresora?: string; // "80mm" | "58mm" (para ancho dinámico)
  esPropio?: boolean; // true si el local es propio
}

export interface TicketConfigApiResponse {
  banca_nombre?: string | null;
  banca_logo?: string | null;
  ticket_nombre_default: string | null;
  ticket_notas_default: string | null;
  ticket_mostrar_logo: boolean | null;
  imprimir_logo?: boolean | null;
  tipo_impresora?: string | null;
  nombre_ticket?: string | null;
  leyenda_ticket?: string | null;
  telefono?: string | null;
  es_propio?: boolean | null;
}

/** Ajustes visuales almacenados en localStorage */
export interface TicketVisualConfig {
  fontWeight?: TicketFontWeight;
  fontSize?: TicketFontSize;
}

// localStorage keys
const LS_VISUAL_LOCAL = 'tiempos_ticket_visual_local';

export function guardarVisualConfig(cfg: TicketVisualConfig): void {
  localStorage.setItem(LS_VISUAL_LOCAL, JSON.stringify(cfg));
  invalidarConfigCache();
}

export function leerVisualConfig(): TicketVisualConfig {
  try {
    const raw = localStorage.getItem(LS_VISUAL_LOCAL);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export interface TicketJugada {
  num1: string;
  num2?: string | null;
  monto: number;
  tipoSorteo?: string; // e.g. 'TICA', 'REVENTADO', 'MONAZOS', 'PARLEY', 'FECHAS', 'MEGA'
}

type BloqueJugadas =
  | { tipo: 'general'; monto: string; lineas: string[] }
  | { tipo: 'monazos'; titulo: string; monto: string; lineas: string[] }
  | { tipo: 'titulo'; texto: string }
  | { tipo: 'reventado-tabla'; filas: Array<{ numeros: string; normal: string; reventado: string }> };

interface PremioTicketLinea {
  etiqueta: string;
  valor: string;
}

export interface TicketLocalInfo {
  nombre: string;
  descripcion?: string | null;
  telefono?: string | null;
  logo?: string | null;
  esPropio?: boolean;
  grupoId?: string | null;
}

export interface TicketPayload {
  ticketCode: string;
  ticketId: string;
  sorteoNombre: string;
  sorteoCierre: string;
  sorteoFecha: string;
  fecha: string;
  vendedorNombre: string;
  localNombre: string;
  local: TicketLocalInfo;
  esPropio: boolean;
  metodoPago: string;
  referencia: string | null;
  total: number;
  jugadas: TicketJugada[];
  config: TicketConfig;
  esReimpresion: boolean;
  tipoTicket: string;
  pagaVecesAplicado: string | null;
  clienteNombre: string;
  fechaOperativa?: string | null;
  estado?: string | null;
  fechaAnulacion?: string | null;
}

export interface ResumenPremiosLinea {
  ticket: string;
  monto: number;
  premio: number;
}

export interface ResumenPremiosPayload {
  local: TicketLocalInfo;
  sorteo: string;
  estado?: string | null;
  fecha: string;
  ganador: string;
  tickets: ResumenPremiosLinea[];
  totalPagado: number;
  totalRecaudado: number;
  sorteoCierre?: string | null;
}

// ─── Config cache ────────────────────────────────────────

let _configCache: TicketConfig | null = null;
let _configCacheTime = 0;
const CONFIG_TTL = 30_000; // 30s

/**
 * Resuelve configuración del LOCAL con fallback a defaults.
 */
export async function resolverTicketConfig(): Promise<TicketConfig> {
  const now = Date.now();
  if (_configCache && now - _configCacheTime < CONFIG_TTL) {
    return _configCache;
  }

  const cfg: TicketConfig = {
    nombre: 'Tiempos V2S General',
    notas: '',
    ancho: 80,
    logoUrl: null,
    logoDataUrl: null,
    mostrarLogo: false,
    fontWeight: 'bold',
    fontSize: 'normal',
    // ── FASE 1: RRR Configuración LOCAL (Logo + Ticket)
    nombreLocal: undefined,
    leyendaLocal: undefined,
    tipoImpresora: '80mm',
  };

  // ── Visual config desde localStorage (local) ──
  const visualLocal = leerVisualConfig();
  cfg.fontWeight = visualLocal.fontWeight || 'bold';
  cfg.fontSize = visualLocal.fontSize || 'normal';

  // ── Config del local (API) ──
  try {
    const local = await fetchAPI<TicketConfigApiResponse>('/operaciones/local/ticket-config');

    // Branding centralizado: solo BANCA define nombre/logo.
    if (local.banca_nombre && cfg.nombre === 'Tiempos V2S General') cfg.nombre = local.banca_nombre;
    if (local.banca_logo) cfg.logoUrl = local.banca_logo;
    cfg.mostrarLogo = false;

    // Configuración operativa del local (permitida)
    if (local.ticket_notas_default && !cfg.notas) cfg.notas = local.ticket_notas_default;
    if (local.leyenda_ticket) cfg.leyendaLocal = local.leyenda_ticket;
    if (local.tipo_impresora) {
      cfg.tipoImpresora = local.tipo_impresora;
      cfg.ancho = local.tipo_impresora === '58mm' ? 58 : 80;
    }
    cfg.esPropio = Boolean(local.es_propio);
  } catch {
    // local config no disponible, usar defaults
  }

  _configCache = { ...cfg };
  _configCacheTime = now;
  return _configCache;
}

/**
 * Invalida la caché de configuración
 */
export function invalidarConfigCache(): void {
  _configCache = null;
  _configCacheTime = 0;
}

// ─── Payload builder ─────────────────────────────────────

export interface CrearPayloadOpts {
  ticketCode: string;
  ticketId: string;
  sorteoNombre: string;
  sorteoCierre: string;
  sorteoFecha?: string;
  metodoPago: string;
  referencia?: string | null;
  total: number;
  jugadas: TicketJugada[];
  vendedorNombre?: string;
  localNombre?: string;
  local?: TicketLocalInfo;
  esPropio?: boolean;
  esReimpresion?: boolean;
  tipoTicket?: string;
  pagaVecesAplicado?: string | null;
  clienteNombre?: string;
  fechaOperativa?: string | null;
  estado?: string | null;
  fechaAnulacion?: string | null;
}

export async function crearTicketPayload(opts: CrearPayloadOpts): Promise<TicketPayload> {
  const config = await resolverTicketConfig();

  const localInfo: TicketLocalInfo = {
    nombre: (opts.local?.nombre || opts.localNombre || '').trim(),
    descripcion: opts.local?.descripcion || null,
    telefono: opts.local?.telefono || null,
    logo: opts.local?.logo || null,
    esPropio: opts.local?.esPropio ?? opts.esPropio ?? false,
    grupoId: opts.local?.grupoId ?? null,
  };

  return {
    ticketCode: opts.ticketCode,
    ticketId: opts.ticketId,
    sorteoNombre: opts.sorteoNombre,
    sorteoCierre: opts.sorteoCierre,
    sorteoFecha: opts.sorteoFecha || '',
    fecha: new Date().toLocaleString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
    vendedorNombre: opts.vendedorNombre || '',
    localNombre: localInfo.nombre,
    local: localInfo,
    esPropio: localInfo.esPropio ?? false,
    metodoPago: opts.metodoPago,
    referencia: opts.referencia || null,
    total: opts.total,
    jugadas: opts.jugadas,
    config,
    esReimpresion: opts.esReimpresion || false,
    tipoTicket: opts.tipoTicket || 'NUEVO',
    pagaVecesAplicado: opts.pagaVecesAplicado ?? null,
    clienteNombre: opts.clienteNombre || 'Cliente',
    fechaOperativa: opts.fechaOperativa || null,
    estado: opts.estado || null,
    fechaAnulacion: opts.fechaAnulacion || null,
  };
}

// ─── HTML Render ─────────────────────────────────────────

/** Mapea TicketFontWeight → CSS font-weight */
function cssWeight(w: TicketFontWeight): string {
  return w === 'extra-bold' ? '900' : w === 'bold' ? '700' : '400';
}

function cssStrongWeight(base: string): string {
  const n = parseInt(base, 10);
  return String(Math.min(900, n + 200));
}

function cssSectionWeight(base: string): string {
  const n = parseInt(base, 10);
  return String(Math.min(900, n + 300));
}

/** Mapea TicketFontSize → tamaños px (optimizados para impresoras térmicas) */
function cssSizes(sz: TicketFontSize, anchoMM: TicketAncho) {
  const map: Record<TicketAncho, Record<TicketFontSize, { body: number; monto: number; nums: number; total: number; nombre: number; seccion: number }>> = {
    58: {
      'normal':       { body: 11, monto: 12, nums: 11, total: 14, nombre: 12, seccion: 11 },
      'grande':       { body: 12, monto: 13, nums: 12, total: 15, nombre: 13, seccion: 12 },
      'extra-grande': { body: 13, monto: 14, nums: 13, total: 16, nombre: 14, seccion: 13 },
      'gigante':      { body: 14, monto: 16, nums: 15, total: 18, nombre: 15, seccion: 14 },
      'extra-gigante':{ body: 15, monto: 18, nums: 17, total: 20, nombre: 17, seccion: 16 },
    },
    80: {
      'normal':       { body: 16, monto: 19, nums: 18, total: 22, nombre: 18, seccion: 17 },
      'grande':       { body: 17, monto: 20, nums: 19, total: 23, nombre: 19, seccion: 18 },
      'extra-grande': { body: 19, monto: 22, nums: 21, total: 25, nombre: 21, seccion: 20 },
      'gigante':      { body: 21, monto: 27, nums: 25, total: 29, nombre: 24, seccion: 22 },
      'extra-gigante':{ body: 23, monto: 31, nums: 29, total: 33, nombre: 27, seccion: 24 },
    },
  };
  return map[anchoMM][sz];
}

// ═══════════════════════════════════════════════════════════════════════════
// PLANTILLA DEDICADA 58mm — Layout optimizado para papel térmico angosto
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Genera HTML específico para ticket 58mm.
 * Diseño compacto, texto legible, sin overflow.
 * NO usa grids complejos — usa texto monoespaciado simple.
 */
function renderTicket58mm(payload: TicketPayload): string {
  const { config, jugadas, esReimpresion } = payload;
  const bloques = buildJugadasBlock(jugadas);
  const premios = parsearPremiosTicket(payload.pagaVecesAplicado);

  // Datos del ticket
  const nombreLocal = (payload.local?.nombre || payload.localNombre || '').trim().toUpperCase();
  const descripcion = (payload.local?.descripcion || '').trim();
  const sorteo = (payload.sorteoNombre || '').trim().toUpperCase();
  const pago = (payload.metodoPago || 'EFECTIVO').toUpperCase();
  const pagaDisplay = payload.pagaVecesAplicado || null;
  const cliente = (payload.clienteNombre || 'Cliente').trim();
  const telefono = (payload.local?.telefono || '').trim();
  const leyenda = config.leyendaLocal || 'GRACIAS POR SU PREFERENCIA';
  const ticketId = payload.ticketId || payload.ticketCode;
  const leyendaFutura = payload.fechaOperativa
    ? `VALIDO PARA EL DIA ${payload.fechaOperativa}`
    : '';

  // Separador de 32 caracteres (ancho útil 58mm)
  const SEP = '--------------------------------';
  const SEP_DOBLE = '================================';

  const logoBlock = '';

  // Bloque reimpresión
  const reimpBlock = esReimpresion
    ? `<div class="reimp">*** REIMPRESION ***</div>`
    : '';

  // ── Marcadores ANULADO ──
  const esAnulado = (payload.estado || '').toUpperCase() === 'ANULADO';
  const fechaAnulMostrar = payload.fechaAnulacion || '—';
  const anuladoBanner = esAnulado
    ? `<div class="anulado-banner" role="alert" style="text-align:center;font-weight:900;font-size:13px;border:2px solid #c00;color:#c00;padding:3px;margin:2px 0;">*** TICKET ANULADO ***</div>`
    : '';
  const anuladoSello = esAnulado
    ? `<div class="anulado-sello" style="text-align:center;font-weight:900;color:#c00;border:1px dashed #c00;padding:2px;margin:2px 0;">** ANULADO **</div>`
    : '';
  const anuladoPie = esAnulado
    ? `<div class="anulado-pie" style="text-align:center;font-size:10px;font-weight:700;color:#c00;margin:2px 0;">*** ESTE TICKET FUE ANULADO — NO VALIDO PARA COBRO ***<br/>Fecha de anulación: ${escHtml(fechaAnulMostrar)}</div>`
    : '';
  const anuladoJugadaCss = esAnulado
    ? `<style>.jugada,.rev,.total{color:#666 !important;text-decoration: line-through;} .anulado-watermark{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;font-size:38px;font-weight:900;color:rgba(192,0,0,0.18);transform:rotate(-30deg);pointer-events:none;letter-spacing:6px;-webkit-print-color-adjust:exact;print-color-adjust:exact;}</style>`
    : '';
  const anuladoWatermark = esAnulado
    ? `<div class="anulado-watermark" aria-hidden="true">ANULADO</div>`
    : '';

  // Generar líneas de jugadas (formato compacto)
  const jugadasHTML = bloques.map((bloque) => {
    if (bloque.tipo === 'titulo') {
      return `<div class="seccion">${escHtml(bloque.texto)}</div>`;
    }
    if (bloque.tipo === 'monazos') {
      return `<div class="seccion">${escHtml(bloque.titulo)}</div>
<div class="jugada"><b>${escHtml(bloque.monto)}</b> ${bloque.lineas.map(l => escHtml(l)).join('<br>')}</div>`;
    }
    if (bloque.tipo === 'reventado-tabla') {
      return `<div class="seccion">NUMS      |DIR  |REV</div>
${bloque.filas.map(f => `<div class="rev">${escHtml(f.numeros.padEnd(10))}|${escHtml(f.normal.padStart(5))}|${escHtml(f.reventado.padStart(4))}</div>`).join('')}`;
    }
    // Jugada normal
    return `<div class="jugada"><b>${escHtml(bloque.monto)}</b> ${bloque.lineas.map(l => escHtml(l)).join('<br>')}</div>`;
  }).join('');

  // Premios si existen
  const premiosHTML = premios.length > 0
    ? `<div class="premios">${premios.map(p => `<div>${escHtml(p.etiqueta)}: ${escHtml(p.valor)}</div>`).join('')}</div>`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Ticket ${payload.ticketCode}</title>
<style>
@page { size: 48mm auto; margin: 0; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
  width: 48mm;
  max-width: 48mm;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.1;
  color: #000;
  background: #fff;
  overflow: hidden;
}
.logo { text-align: center; margin: 0; }
.logo img { width: 100%; max-height: 70px; }
.local { text-align: center; font-size: 14px; font-weight: 900; }
.desc { text-align: center; font-size: 11px; }
.sorteo { text-align: center; font-size: 13px; font-weight: 900; margin: 1px 0; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 1px 0; }
.sep { color: #666; font-size: 10px; overflow: hidden; }
.info { font-size: 11px; }
.info b { font-weight: 900; }
.reimp { text-align: center; font-weight: 900; border: 1px dashed #000; margin: 1px 0; font-size: 11px; }
.seccion { font-size: 12px; font-weight: 900; margin-top: 1px; border-bottom: 1px dotted #999; }
.jugada { font-size: 13px; margin: 1px 0; word-break: break-word; letter-spacing: 0.3px; }
.jugada b { font-weight: 900; }
.rev { font-size: 12px; font-family: 'Courier New', Courier, monospace; letter-spacing: 0.2px; }
.premios { font-size: 11px; margin: 1px 0; }
.total { font-size: 15px; font-weight: 900; margin: 1px 0; }
.nota { font-size: 10px; }
.leyenda { text-align: center; font-size: 10px; font-weight: 700; }
.ticket-id { font-size: 10px; font-weight: 700; }
@media print {
  html { height: auto !important; }
  body { margin: 0; padding: 0 0 2px; height: auto !important; min-height: 0 !important; }
}
</style>
${anuladoJugadaCss}
</head>
<body>
${anuladoWatermark}
${anuladoBanner}
${reimpBlock}
${logoBlock}
<div class="local">${escHtml(nombreLocal)}</div>
${descripcion ? `<div class="desc">${escHtml(descripcion)}</div>` : ''}
${sorteo ? `<div class="sorteo">${escHtml(sorteo)}</div>` : ''}
<div class="sep">${SEP}</div>
<div class="info"><b>VTA:</b> ${escHtml(payload.fecha)}</div>
${pagaDisplay ? `<div class="info"><b>Paga:</b> ${escHtml(pagaDisplay)}</div>` : ''}
<div class="info"><b>CLI:</b> ${escHtml(cliente)}</div>
${telefono ? `<div class="info"><b>TEL:</b> ${escHtml(telefono)}</div>` : ''}
<div class="sep">${SEP}</div>
<div class="seccion">DETALLE</div>
${jugadasHTML}
<div class="sep">${SEP}</div>
${premiosHTML}
<div class="total">TOTAL: ₡${payload.total.toLocaleString('es-CR')}</div>
${anuladoSello}
<div class="sep">${SEP}</div>
${leyendaFutura ? `<div class="leyenda" style="font-size:12px;font-weight:900;border:1px dashed #000;padding:2px;margin:2px 0;">${escHtml(leyendaFutura)}</div>` : ''}
<div class="nota">${escHtml(leyenda)}</div>
<div class="ticket-id">TKT: ${escHtml(ticketId)}</div>
${anuladoPie}
</body>
</html>`;
}

/**
 * Genera HTML completo del ticket térmico en formato Barahona.
 * Solo afecta presentación; no modifica lógica de ventas ni backend.
 */
export function renderTicketHTML(payload: TicketPayload): string {
  const { config } = payload;
  const anchoMM = config.ancho;

  // ── PLANTILLA DEDICADA PARA 58mm ──
  if (anchoMM === 58) {
    return renderTicket58mm(payload);
  }

  // ── PLANTILLA 80mm (sin cambios) ──
  const { jugadas, esReimpresion } = payload;
  // ── Valores fijos para 80mm ──
  const anchoPx = 300;
  const charWidth = 34;
  const colMeta = 76;
  const colMonto = 72;
  const colRev = 68;
  const sz = cssSizes(config.fontSize, 80);
  const weight = cssWeight(config.fontWeight);
  const weightStrong = cssStrongWeight(weight);
  const bloques = buildJugadasBlock(jugadas);
  const esReventadoTabla = bloques.some(b => b.tipo === 'reventado-tabla');
  const premios = parsearPremiosTicket(payload.pagaVecesAplicado);

  // ── Encabezado por local activo ──
  const nombreMostrar = (payload.local?.nombre || payload.localNombre || '').trim().toUpperCase();
  const descripcionMostrar = (payload.local?.descripcion || '').trim();
  const telefonoMostrar = (payload.local?.telefono || '').trim();
  const clienteMostrar = (payload.clienteNombre || 'Cliente').trim();
  const vendedorMostrar = (payload.vendedorNombre || '').trim();
  // ── Sorteo y método de pago ──
  const sorteoMostrar = (payload.sorteoNombre || '').trim();
  const pagoMostrar = (payload.metodoPago || 'EFECTIVO').toUpperCase();
  const pagaDisplay = payload.pagaVecesAplicado || null;

  const logoBlock = '';

  // ── Reimpresión ──
  const reimpBlock = esReimpresion
    ? `<div class="reimp">******** REIMPRESIÓN ********</div>`
    : '';

  // ── Disclaimer reimpresión ──
  const reimpDisclaimer = '';

  // ── FASE 1: RRR Ajuste PRO #2 — leyenda configurable local ──
  const leyendaMostrar = config.leyendaLocal || 'GRACIAS POR SU PREFERENCIA';
  const leyendaFutura80 = payload.fechaOperativa
    ? `VALIDO PARA EL DIA ${payload.fechaOperativa}`
    : '';

  // ── Marcadores ANULADO (80mm) ──
  const esAnulado80 = (payload.estado || '').toUpperCase() === 'ANULADO';
  const fechaAnulMostrar80 = payload.fechaAnulacion || '—';
  const anuladoBanner80 = esAnulado80
    ? `<div class="anulado-banner" role="alert" style="text-align:center;font-weight:900;font-size:16px;border:2px solid #c00;color:#c00;padding:4px;margin:4px 0;-webkit-print-color-adjust:exact;print-color-adjust:exact;">*** TICKET ANULADO ***</div>`
    : '';
  const anuladoSello80 = esAnulado80
    ? `<div class="anulado-sello" style="text-align:center;font-weight:900;color:#c00;border:1px dashed #c00;padding:3px;margin:4px 0;-webkit-print-color-adjust:exact;print-color-adjust:exact;">** ANULADO **</div>`
    : '';
  const anuladoPie80 = esAnulado80
    ? `<div class="anulado-pie" style="text-align:center;font-size:11px;font-weight:700;color:#c00;margin:4px 0;-webkit-print-color-adjust:exact;print-color-adjust:exact;">*** ESTE TICKET FUE ANULADO — NO VALIDO PARA COBRO ***<br/>Fecha de anulación: ${escHtml(fechaAnulMostrar80)}</div>`
    : '';
  const anuladoCss80 = esAnulado80
    ? `<style>.anulado-jugada,.row,.rev-row,.total{color:#666 !important;text-decoration: line-through;} .anulado-watermark{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;font-size:64px;font-weight:900;color:rgba(192,0,0,0.15);transform:rotate(-30deg);pointer-events:none;letter-spacing:10px;-webkit-print-color-adjust:exact;print-color-adjust:exact;z-index:1;}</style>`
    : '';
  const anuladoWatermark80 = esAnulado80
    ? `<div class="anulado-watermark" aria-hidden="true">ANULADO</div>`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Ticket ${payload.ticketCode}</title>
<style>
@page { size: 80mm auto; margin: 0; }
* { margin:0; padding:0; box-sizing:border-box; }
body {
  width: 80mm;
  max-width: 80mm;
  overflow: hidden;
  word-break: break-word;
  font-family: 'Courier New', Courier, monospace;
  font-size: ${sz.body}px;
  font-weight: ${weight};
  line-height: 1.15;
  color: #000;
  background: #fff;
  padding: 0 4px 2px;
  margin: 0 auto;
}
.ticket {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  word-break: break-word;
  font-family: 'Courier New', Courier, monospace;
  font-size: ${sz.body}px;
  line-height: 1.15;
}
.center { text-align: center; }
.reimp {
  text-align: center;
  padding: 1px 0;
  border: 1px dashed #333;
  margin-bottom: 0;
}
.title {
  font-size: ${Math.max(13, sz.nombre)}px;
  font-weight: ${weightStrong};
}
.sub-title {
  font-size: ${Math.max(12, sz.body)}px;
  margin-bottom: 0;
}
.line {
  margin-top: 0;
  margin-bottom: 0;
}
.meta-box {
  margin: 0;
}
.meta-row {
  display: grid;
  grid-template-columns: ${colMeta}px 1fr;
  column-gap: 4px;
  align-items: baseline;
  margin: 0;
}
.meta-label {
  font-weight: ${weightStrong};
}
.meta-value {
  text-align: left;
}
.sorteo-destacado {
  text-align: center;
  font-size: ${Math.max(14, sz.seccion + 2)}px;
  font-weight: ${cssSectionWeight(weight)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
  padding: 0;
  border-top: 1px dashed #000;
  border-bottom: 1px dashed #000;
}
.spacer {
  height: 0;
}
.sep {
  border: 0;
  border-top: 1px dashed #000;
  margin: 0;
}
.detalle {
  font-weight: ${weightStrong};
  text-align: center;
}
.row {
  display: grid;
  grid-template-columns: ${colMonto}px 1fr;
  align-items: baseline;
  text-align: left;
  column-gap: 4px;
  margin: 0;
}
.header {
  font-weight: ${weightStrong};
  margin-bottom: 0;
}
.monto {
  width: ${colMonto}px;
  flex: 0 0 ${colMonto}px;
  font-weight: ${weightStrong};
}
.nums {
  flex: 1;
  word-break: break-word;
}
.tipo-seccion {
  text-align: left;
  font-weight: ${weightStrong};
  margin: 0;
}
.premios-box {
  margin: 0;
}
.premio-row {
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 4px;
  margin: 0;
}
.premio-label {
  text-align: left;
}
.premio-valor {
  text-align: right;
  font-weight: ${weightStrong};
}
.total {
  font-size: ${sz.total}px;
  font-weight: ${weightStrong};
}
.center-note {
  text-align: center;
  margin: 0;
}
.footer {
  margin-top: 0;
}
.reimp-disclaimer {
  text-align: left;
  margin: 0;
}
.rev-tabla {
  margin: 0;
}
.rev-header, .rev-row {
  display: grid;
  grid-template-columns: 1fr 4px ${colRev}px 4px ${colRev}px;
  column-gap: 0;
  align-items: center;
}
.rev-header {
  font-weight: ${weightStrong};
  font-size: ${Math.max(10, sz.nums - 2)}px;
}
.rev-nums {
  text-align: left;
}
.rev-sep {
  text-align: center;
  color: #666;
}
.rev-col {
  text-align: center;
  font-size: ${Math.max(10, sz.nums - 1)}px;
}
@media print {
  html { height: auto !important; overflow: hidden !important; }
  body { margin: 0; padding: 0 3px 2px; height: auto !important; min-height: 0 !important; max-height: max-content !important; overflow: hidden !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .ticket { font-weight: inherit; height: auto !important; }
}
</style>
${anuladoCss80}
</head>
<body>
<div class="ticket">
${anuladoWatermark80}
${anuladoBanner80}
${reimpBlock}
${logoBlock}
<div class="center title">${escHtml(nombreMostrar || config.nombre || '')}</div>
${descripcionMostrar ? `<div class="center sub-title">${escHtml(descripcionMostrar)}</div>` : ''}
${sorteoMostrar ? `<div class="sorteo-destacado">${escHtml(sorteoMostrar)}</div>` : ''}

<div class="meta-box">
  <div class="meta-row"><span class="meta-label">VENTA:</span><span class="meta-value">${escHtml(payload.fecha)}</span></div>
  ${pagaDisplay ? `<div class="meta-row"><span class="meta-label">Paga:</span><span class="meta-value">${escHtml(pagaDisplay)}</span></div>` : ''}
  <div class="meta-row"><span class="meta-label">CLIENTE:</span><span class="meta-value">${escHtml(clienteMostrar)}</span></div>
  ${vendedorMostrar ? `<div class="meta-row"><span class="meta-label">VENDEDOR:</span><span class="meta-value">${escHtml(vendedorMostrar)}</span></div>` : ''}
  ${telefonoMostrar ? `<div class="meta-row"><span class="meta-label">TEL:</span><span class="meta-value">${escHtml(telefonoMostrar)}</span></div>` : ''}
</div>

<div class="spacer"></div>

<hr class="sep"/>
<div class="detalle">DETALLE</div>
<hr class="sep"/>

${!esReventadoTabla ? `<div class="row header">
  <span class="monto">MONTO</span>
  <span class="nums">NUMEROS</span>
</div>
<hr class="sep"/>` : ''}

${bloques.map((bloque) => {
  if (bloque.tipo === 'titulo') {
    return `<div class="tipo-seccion">${escHtml(bloque.texto)}</div>`;
  }
  if (bloque.tipo === 'monazos') {
    const [primera, ...extra] = bloque.lineas;
    return `<div class="tipo-seccion">${escHtml(bloque.titulo)}</div><div class="row"><span class="monto">${escHtml(bloque.monto)}</span><span class="nums">${escHtml(primera || '')}</span></div>${extra.map((linea) => `<div class="row"><span class="monto"></span><span class="nums">${escHtml(linea)}</span></div>`).join('')}<div class="spacer"></div>`;
  }
  if (bloque.tipo === 'reventado-tabla') {
    return `<div class="rev-tabla">
<div class="rev-header"><span class="rev-nums">NUMEROS</span><span class="rev-sep">|</span><span class="rev-col">DIR</span><span class="rev-sep">|</span><span class="rev-col">REV</span></div>
<hr class="sep"/>
${bloque.filas.map((fila) => `<div class="rev-row"><span class="rev-nums">${escHtml(fila.numeros)}</span><span class="rev-sep">|</span><span class="rev-col">${escHtml(fila.normal)}</span><span class="rev-sep">|</span><span class="rev-col">${escHtml(fila.reventado)}</span></div>`).join('')}
</div>`;
  }

  const [primera, ...extra] = bloque.lineas;
  return `<div class="row"><span class="monto">${escHtml(bloque.monto)}</span><span class="nums">${escHtml(primera || '')}</span></div>${extra.map((linea) => `<div class="row"><span class="monto"></span><span class="nums">${escHtml(linea)}</span></div>`).join('')}<div class="spacer"></div>`;
}).join('')}

<hr class="sep"/>
<div class="total">TOTAL: ₡${payload.total.toLocaleString('es-CR')}</div>
${anuladoSello80}

<hr class="sep"/>

${leyendaFutura80 ? `<div class="center-note" style="font-weight:900;border:1px dashed #000;padding:2px;margin:2px 0;">${escHtml(leyendaFutura80)}</div>` : ''}
<div class="center-note">${escHtml(leyendaMostrar)}</div>

<div class="line">TIQUETE: ${escHtml(payload.ticketId || payload.ticketCode)}</div>
${reimpDisclaimer}
${anuladoPie80}
</div>
</body>
</html>`;
}

/**
 * Agrupa jugadas por monto → bloques limpios.
 *
 * Formato inline (monto y números en misma línea):
 *   ₡1,000   22 23 24 25 26 27 28
 *
 * Si los números no caben, continúan en la siguiente línea
 * alineados bajo los números (no bajo el monto).
 *
 * Formatea según tipo de sección:
 *  - REVENTADO: 22(R1) 23(R2)
 *  - MEGA: extrae número mega de num2
 *  - PARLEY: num1+num2
 *  - MONAZOS: número con tipo
 *  - NORMAL/FECHAS: num1 directo
 */
function buildJugadasBlock(jugadas: TicketJugada[]): BloqueJugadas[] {
  if (!jugadas.length) return [];

  const monazoLabels: Record<string, string> = {
    ORDEN: 'En Orden',
    DESORDEN: 'Desorden',
    P2: 'Primeros 2',
    U2: 'Ultimos 2',
  };

  // Detectar MONAZOS: todos los num2 son un tipo de monazo
  const esMonazos = jugadas.every((jugada) => !!jugada.num2 && Object.prototype.hasOwnProperty.call(monazoLabels, jugada.num2));

  if (esMonazos) {
    const ordenTipos = ['ORDEN', 'DESORDEN', 'P2', 'U2'];
    // Agrupar por tipo + monto para luego hacer bloques de 4
    const grupos = new Map<string, Map<number, string[]>>();

    for (const jugada of jugadas) {
      const tipo = jugada.num2 as string;
      if (!grupos.has(tipo)) grupos.set(tipo, new Map());
      const porMonto = grupos.get(tipo)!;
      const lista = porMonto.get(jugada.monto) || [];
      lista.push(jugada.num1);
      porMonto.set(jugada.monto, lista);
    }

    const bloques: BloqueJugadas[] = [];
    for (const tipo of ordenTipos) {
      const porMonto = grupos.get(tipo);
      if (!porMonto) continue;
      for (const [monto, numeros] of porMonto) {
        const lineas: string[] = [];
        for (let i = 0; i < numeros.length; i += 4) {
          lineas.push(numeros.slice(i, i + 4).join(' | '));
        }
        bloques.push({
          tipo: 'monazos' as const,
          titulo: monazoLabels[tipo],
          monto: '₡' + monto.toLocaleString('es-CR'),
          lineas,
        });
      }
    }
    return bloques;
  }

  // Detectar REVENTADO: alguna jugada tiene num2 = 'REV1' o 'REV2'
  const esReventado = jugadas.some(j => j.num2 === 'REV1' || j.num2 === 'REV2');

  if (esReventado) {
    // FIX: preserve duplicate numbers — build rows by pairing REV1+REV2 for the same
    // number occurrence, but create a NEW row for each additional occurrence.
    // The previous Map<num, {directo,reventado}> approach collapsed duplicates by summing:
    //   two jugadas "49/REV1/300" → Map showed 49: DIR ₡600 (one row, amount doubled).
    // Now we use an ordered array so each occurrence gets its own display slot.
    type RevRow = { num: string; directo: number | null; reventado: number | null };
    const rows: RevRow[] = [];
    for (const jugada of jugadas) {
      const num = jugada.num1;
      const isRev2 = jugada.num2 === 'REV2';
      // Find an incomplete row for this num that still needs this slot (directo or reventado)
      const idx = rows.findIndex(r =>
        r.num === num && (isRev2 ? r.reventado === null : r.directo === null)
      );
      if (idx >= 0) {
        // Pair into existing row (REV1+REV2 for the same bet on the same number)
        if (isRev2) rows[idx].reventado = jugada.monto;
        else rows[idx].directo = jugada.monto;
      } else {
        // New occurrence — always creates a separate row, preserving the duplicate
        rows.push({
          num,
          directo: isRev2 ? null : jugada.monto,
          reventado: isRev2 ? jugada.monto : null,
        });
      }
    }

    // Paso 2: Agrupar filas con idéntica combinación de montos (display compacto, 4 por línea)
    const porMontoCombo = new Map<string, string[]>();
    for (const row of rows) {
      const key = `${row.directo ?? ''}_${row.reventado ?? ''}`;
      if (!porMontoCombo.has(key)) porMontoCombo.set(key, []);
      porMontoCombo.get(key)!.push(row.num);  // duplicate nums appear here as separate entries
    }

    // Paso 3: Construir filas (agrupar hasta 4 números por fila)
    const filas: Array<{ numeros: string; normal: string; reventado: string }> = [];

    for (const [key, numeros] of porMontoCombo) {
      const [directoStr, reventadoStr] = key.split('_');
      const directo = directoStr ? Number(directoStr) : null;
      const reventado = reventadoStr ? Number(reventadoStr) : null;

      // Agrupar de 4 en 4
      for (let i = 0; i < numeros.length; i += 4) {
        const grupo = numeros.slice(i, i + 4);
        filas.push({
          numeros: grupo.join('-'),
          normal: directo !== null ? '₡' + directo.toLocaleString('es-CR') : '',
          reventado: reventado !== null ? '₡' + reventado.toLocaleString('es-CR') : '',
        });
      }
    }

    return [{ tipo: 'reventado-tabla', filas }];
  }

  // General: NORMAL/TICA, FECHAS, PARLEY, MEGA (directo)
  return _agruparPorMonto(jugadas);
}

/** Agrupa jugadas por monto formando bloques de hasta 4 números por línea. */
function _agruparPorMonto(jugadas: TicketJugada[]): Array<{ tipo: 'general'; monto: string; lineas: string[] }> {
  const montoMap = new Map<number, string[]>();
  for (const jugada of jugadas) {
    const lista = montoMap.get(jugada.monto) || [];
    lista.push(numeroParaTicket(jugada));
    montoMap.set(jugada.monto, lista);
  }
  const bloques: Array<{ tipo: 'general'; monto: string; lineas: string[] }> = [];
  for (const [monto, numeros] of montoMap) {
    const lineas: string[] = [];
    for (let i = 0; i < numeros.length; i += 4) {
      lineas.push(numeros.slice(i, i + 4).join(' | '));
    }
    bloques.push({ tipo: 'general', monto: '₡' + monto.toLocaleString('es-CR'), lineas });
  }
  return bloques;
}

const MESES_CORTO: Record<string, string> = {
  '01': 'Ene', '02': 'Febr', '03': 'Marz', '04': 'Abr',
  '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Set', '10': 'Oct', '11': 'Nov', '12': 'Dic',
};

function numeroParaTicket(jugada: TicketJugada): string {
  if (jugada.num2 && /^MEGA:/i.test(jugada.num2)) {
    return jugada.num2.replace(/^MEGA:/i, '');
  }
  if (jugada.num2 && /^\d{2}$/.test(jugada.num2)) {
    return `${jugada.num1}-${jugada.num2}`;
  }
  // FECHAS: convertir DD-MM → DD-Mes
  if (jugada.tipoSorteo === 'FECHAS') {
    const match = jugada.num1.match(/^(\d{1,2})-(\d{2})$/);
    if (match) {
      const mes = MESES_CORTO[match[2]] || match[2];
      return `${match[1]}-${mes}`;
    }
  }
  return jugada.num1;
}

function parsearPremiosTicket(pagaVecesAplicado: string | null): PremioTicketLinea[] {
  if (!pagaVecesAplicado) return [];

  return pagaVecesAplicado
    .split('|')
    .map((parte) => parte.trim())
    .filter(Boolean)
    .map((parte) => {
      const match = parte.match(/^(.*?)(\d+(?:\.\d+)?)$/);
      if (!match) {
        return null;
      }

      return {
        etiqueta: match[1].trim(),
        valor: match[2].trim(),
      };
    })
    .filter((parte): parte is PremioTicketLinea => !!parte && parte.etiqueta.length > 0 && parte.valor.length > 0);
}

/** Escapa HTML básico */
function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ─── Inline render (para preview dentro de React) ────────

/**
 * Genera el HTML del ticket body (sin <html>/<head>) para usar dentro de un iframe srcdoc.
 * Usa barcode inline vía canvas.
 */
export function renderTicketPreviewHTML(payload: TicketPayload): string {
  // Usamos el HTML completo para el iframe
  return renderTicketHTML(payload);
}

// ─── Impresión ───────────────────────────────────────────

/**
 * Abre ventana temporal, imprime y la cierra automáticamente.
 * Usa onafterprint + fallback setTimeout para compatibilidad.
 */
function abrirVentanaImpresion(html: string, ancho: number): boolean {
  const w = ancho === 58 ? 420 : 560;
  const printWindow = window.open('', '_blank', `width=${w},height=700`);
  if (!printWindow) {
    console.error('[ticketService] No se pudo abrir ventana de impresión');
    return false;
  }

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  // Cerrar ventana después de imprimir (o cancelar)
  let cerrada = false;
  const cerrar = () => {
    if (cerrada) return;
    cerrada = true;
    try { printWindow.close(); } catch (_) { /* ya cerrada */ }
    // Firefox: close() puede fallar silenciosamente; limpiar si sigue abierta
    setTimeout(() => {
      try {
        if (!printWindow.closed) {
          printWindow.location.replace('about:blank');
          // Reintentar close sobre about:blank (Firefox lo permite a veces)
          setTimeout(() => {
            try { if (!printWindow.closed) printWindow.close(); } catch (_) { /* ignorar */ }
          }, 150);
        }
      } catch (_) { /* ventana inaccesible */ }
    }, 300);
  };

  printWindow.onload = () => {
    // onafterprint: se dispara al terminar/cancelar el diálogo de impresión
    if ('onafterprint' in printWindow) {
      printWindow.onafterprint = cerrar;
    }

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      // Fallback: si onafterprint no disparó, cerrar tras breve espera
      setTimeout(cerrar, 2000);
    }, 400);
  };

  return true;
}

/**
 * Imprime un ticket individual.
 * Abre ventana temporal, imprime y cierra automáticamente.
 */
export function imprimirTicket(payload: TicketPayload): boolean {
  const html = renderTicketHTML(payload);
  return abrirVentanaImpresion(html, payload.config.ancho);
}

function extraerStyleTicket(html: string): string {
  const match = html.match(/<style>([\s\S]*?)<\/style>/i);
  return match?.[1] || '';
}

function extraerBodyTicket(html: string): string {
  const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return match?.[1] || html;
}

export function imprimirTicketsLote(payloads: TicketPayload[]): boolean {
  if (!payloads.length) {
    return false;
  }
  if (payloads.length === 1) {
    return imprimirTicket(payloads[0]);
  }

  const htmlBase = renderTicketHTML(payloads[0]);
  const styles = `${extraerStyleTicket(htmlBase)}\n.page-break { page-break-after: always; }\n.page-break:last-child { page-break-after: auto; }`;
  const bloques = payloads
    .map((payload, index) => {
      const body = extraerBodyTicket(renderTicketHTML(payload));
      return `${body}${index < payloads.length - 1 ? '<div class="page-break"></div>' : ''}`;
    })
    .join('');

  const printWindow = window.open('', '_blank', `width=${payloads[0].config.ancho === 58 ? 420 : 560},height=700`);
  if (!printWindow) {
    console.error('[ticketService] No se pudo abrir ventana de impresión para lote');
    return false;
  }

  printWindow.document.open();
  printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Impresión de premios</title><style>${styles}</style></head><body>${bloques}</body></html>`);
  printWindow.document.close();

  let cerrada = false;
  const cerrar = () => {
    if (cerrada) return;
    cerrada = true;
    try { printWindow.close(); } catch (_) { /* ya cerrada */ }
    // Firefox: close() puede fallar silenciosamente; limpiar si sigue abierta
    setTimeout(() => {
      try {
        if (!printWindow.closed) {
          printWindow.location.replace('about:blank');
          setTimeout(() => {
            try { if (!printWindow.closed) printWindow.close(); } catch (_) { /* ignorar */ }
          }, 150);
        }
      } catch (_) { /* ventana inaccesible */ }
    }, 300);
  };

  printWindow.onload = () => {
    if ('onafterprint' in printWindow) {
      printWindow.onafterprint = cerrar;
    }
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      setTimeout(cerrar, 2000);
    }, 400);
  };
  return true;
}

function padRight(text: string, width: number): string {
  const value = text.length > width ? text.slice(0, width) : text;
  return value.padEnd(width, ' ');
}

function padLeft(text: string, width: number): string {
  const value = text.length > width ? text.slice(0, width) : text;
  return value.padStart(width, ' ');
}

function formatResumenMonto(value: number): string {
  return Math.round(Number(value || 0)).toString();
}

function renderResumenRows(lineas: ResumenPremiosLinea[]): string {
  return lineas.map((linea) => {
    const ticket = padRight(linea.ticket, 14);
    const monto = padLeft(formatResumenMonto(linea.monto), 8);
    const premio = padLeft(formatResumenMonto(linea.premio), 10);
    return `${ticket}${monto}${premio}`;
  }).join('\n');
}

export function renderResumenPremiosHTML(payload: ResumenPremiosPayload): string {
  const fechaImpresion = new Date().toLocaleString('es-CR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const localNombre = payload.local.nombre || '';
  const localDescripcion = payload.local.descripcion || '';
  const sorteoDetalle = payload.sorteoCierre ? `${payload.sorteo} ${payload.sorteoCierre}` : payload.sorteo;
  const totalPagado = formatResumenMonto(payload.totalPagado);
  const totalRecaudado = formatResumenMonto(payload.totalRecaudado);

  // Generar filas HTML directamente desde payload.tickets
  const filasHTML = payload.tickets.length > 0
    ? payload.tickets.map((t) => {
        const ticketCorto = t.ticket.length > 10 ? t.ticket.slice(-10) : t.ticket;
        const monto = formatResumenMonto(t.monto);
        const premio = formatResumenMonto(t.premio);
        return `<div class="tk-row"><span class="tk-id">#${escHtml(ticketCorto)}</span><span class="tk-val">₡${escHtml(monto)}</span><span class="tk-val tk-premio">₡${escHtml(premio)}</span></div>`;
      }).join('')
    : '<div class="center" style="margin:4px 0">SIN TICKETS</div>';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Resumen de premios</title>
<style>
@page { size: 58mm auto; margin: 0; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
  width: 58mm;
  max-width: 58mm;
  overflow-x: hidden;
  overflow-wrap: break-word;
  word-break: break-word;
  margin: 0;
  padding: 2px 3px 6px;
  background: #fff;
  color: #000;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
}
.center { text-align: center; }
.logo { text-align: center; margin: 0; padding: 0; line-height: 0; }
.logo img { width: 100%; max-height: 60px; object-fit: contain; }
.title { font-size: 14px; font-weight: 900; margin-top: 2px; }
.subtitle { font-size: 12px; font-weight: 700; }
.sep { border: 0; border-top: 1px dashed #000; margin: 3px 0; }
.meta-box { margin: 2px 0; }
.meta-row {
  display: grid;
  grid-template-columns: 70px 1fr;
  column-gap: 4px;
  align-items: baseline;
  margin: 1px 0;
}
.meta-label { font-weight: 900; font-size: 11px; }
.meta-value { text-align: left; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tk-header {
  display: grid;
  grid-template-columns: 1fr 55px 55px;
  column-gap: 2px;
  font-weight: 900;
  font-size: 11px;
  margin: 2px 0;
}
.tk-header span:nth-child(2),
.tk-header span:nth-child(3) { text-align: right; }
.tk-row {
  display: grid;
  grid-template-columns: 1fr 55px 55px;
  column-gap: 2px;
  font-size: 10px;
  font-weight: 700;
  margin: 1px 0;
  border-bottom: 1px dotted #ccc;
  padding: 1px 0;
}
.tk-id { font-weight: 900; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tk-val { text-align: right; font-weight: 700; white-space: nowrap; }
.tk-premio { font-weight: 900; }
.totales { margin: 3px 0; }
.total-row {
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 4px;
  font-weight: 900;
  font-size: 13px;
  margin: 2px 0;
}
.total-row span:last-child { text-align: right; white-space: nowrap; }
.footer { margin-top: 3px; font-size: 11px; font-weight: 700; }
.fecha-imp { font-size: 10px; font-weight: 700; margin-top: 2px; }
@media print {
  body { margin: 0; padding: 1px 2px 4px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
</style>
</head>
<body>
  <div class="center title">${escHtml(localNombre)}</div>
  ${localDescripcion ? `<div class="center subtitle">${escHtml(localDescripcion)}</div>` : ''}

  <hr class="sep"/>

  <div class="meta-box">
    <div class="meta-row"><span class="meta-label">FECHA:</span><span class="meta-value">${escHtml(payload.fecha)}</span></div>
    <div class="meta-row"><span class="meta-label">JUEGO:</span><span class="meta-value">${escHtml(sorteoDetalle)}</span></div>
    <div class="meta-row"><span class="meta-label">ESTADO:</span><span class="meta-value">${escHtml(payload.estado || 'CERRADO')}</span></div>
    <div class="meta-row"><span class="meta-label">GANADOR:</span><span class="meta-value">${escHtml(payload.ganador)}</span></div>
  </div>

  <hr class="sep"/>
  <div class="tk-header"><span>TICKET</span><span>MONTO</span><span>PREMIO</span></div>
  <hr class="sep"/>
  ${filasHTML}
  <hr class="sep"/>

  <div class="totales">
    <div class="total-row"><span>TOTAL:</span><span>${escHtml(totalPagado)}</span></div>
  </div>

  <hr class="sep"/>

  <div class="totales">
    <div class="total-row"><span>RECAUDADO:</span><span>${escHtml(totalRecaudado)}</span></div>
    <div class="total-row"><span>PAGADO:</span><span>${escHtml(totalPagado)}</span></div>
  </div>

  <hr class="sep"/>
  <div class="center footer">GRACIAS POR SU PREFERENCIA</div>
  <div class="center fecha-imp">${escHtml(fechaImpresion)}</div>
</body>
</html>`;
}

export function imprimirResumenPremios(payload: ResumenPremiosPayload): boolean {
  const html = renderResumenPremiosHTML(payload);
  return abrirVentanaImpresion(html, 58);
}

// ─── Post-venta: flujo único ─────────────────────────────

export interface PostVentaParams {
  ticketCode: string;
  ticketId: string;
  sorteoNombre: string;
  sorteoCierre: string;
  sorteoFecha?: string;
  metodoPago: string;
  referencia?: string | null;
  total: number;
  jugadas: TicketJugada[];
  vendedorNombre?: string;
  localNombre?: string;
  local?: TicketLocalInfo;
  esPropio?: boolean;
  pagaVecesAplicado?: string | null;
  clienteNombre?: string;
  fechaOperativa?: string | null;
  estado?: string | null;
  fechaAnulacion?: string | null;
}

/**
 * procesarPostVenta: Punto de entrada único post-venta.
 * Genera el payload y lo retorna para que el UI decida mostrar preview.
 * 
 * NO modifica lógica de venta. Solo genera el payload del ticket.
 */
export async function procesarPostVenta(params: PostVentaParams): Promise<TicketPayload> {
  const payload = await crearTicketPayload({
    ...params,
    esReimpresion: false,
  });

  return payload;
}

/**
 * procesarReimpresion: Genera payload para reimpresión con marca.
 */
export async function procesarReimpresion(params: PostVentaParams): Promise<TicketPayload> {
  const payload = await crearTicketPayload({
    ...params,
    esReimpresion: true,
  });

  return payload;
}

// ─── Helpers ─────────────────────────────────────────────

function wrapText(texto: string, ancho: number): string {
  const words = texto.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if ((current + ' ' + word).trim().length > ancho) {
      if (current) lines.push(current);
      current = word;
    } else {
      current = current ? `${current} ${word}` : word;
    }
  }
  if (current) lines.push(current);
  return lines.join('\n');
}
